import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BoxedTitle from "@/components/BoxedTitle";
import { readCache, writeCache, clearCache } from "@/pages/admin/adminCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";

/**
 * /admin — top-level dashboard. Lists every admin section with a
 * one-glance summary (unread inbox count, high-threat competitor
 * count). Each section card is the entry point to its own subroute.
 *
 * Cached counts render instantly while a fresh fetch runs in the
 * background — see adminCache.js.
 */
export default function AdminHome() {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const cached = readCache();
  const [unread, setUnread] = useState(cached.inbox?.unread ?? null);
  const [totalContacts, setTotalContacts] = useState(cached.inbox?.total ?? null);
  const [highThreat, setHighThreat] = useState(cached.market?.high ?? null);
  const [totalCompetitors, setTotalCompetitors] = useState(
    cached.market?.total ?? null
  );
  const [essayCount, setEssayCount] = useState(cached.writing?.essays ?? null);
  const [referenceCount, setReferenceCount] = useState(
    cached.writing?.references ?? null
  );
  const [ideaCount, setIdeaCount] = useState(cached.writing?.ideas ?? null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!key) {
      navigate("/login?next=/admin", { replace: true });
      return;
    }
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/contact`, { headers: { "X-Admin-Key": key } }),
      axios.get(`${API}/admin/market/competitors`, {
        headers: { "X-Admin-Key": key },
      }),
      axios.get(`${API}/admin/essays`, { headers: { "X-Admin-Key": key } }),
      axios.get(`${API}/admin/references`, {
        headers: { "X-Admin-Key": key },
      }),
      axios.get(`${API}/admin/ideas`, { headers: { "X-Admin-Key": key } }),
    ])
      .then(([contactsRes, competitorsRes, essaysRes, referencesRes, ideasRes]) => {
        if (cancelled) return;
        // Bail if user signed out mid-flight (race: handler runs sync,
        // resolves pending microtasks before React unmounts us).
        if (!sessionStorage.getItem(KEY_STORAGE)) return;
        const contacts = contactsRes.data || [];
        const competitors = competitorsRes.data || [];
        const essays = essaysRes.data || [];
        const references = referencesRes.data || [];
        const ideas = ideasRes.data || [];
        const inbox = {
          total: contacts.length,
          unread: contacts.filter((c) => !c.is_read).length,
        };
        const market = {
          total: competitors.length,
          high: competitors.filter((c) => c.threat === "high").length,
        };
        const writing = {
          essays: essays.length,
          references: references.length,
          ideas: ideas.length,
        };
        setTotalContacts(inbox.total);
        setUnread(inbox.unread);
        setTotalCompetitors(market.total);
        setHighThreat(market.high);
        setEssayCount(writing.essays);
        setReferenceCount(writing.references);
        setIdeaCount(writing.ideas);
        writeCache({ inbox, market, writing });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          sessionStorage.removeItem(KEY_STORAGE);
          clearCache();
          setKey("");
          navigate("/login?next=/admin", { replace: true });
        } else {
          setError("Could not load summary.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [key, navigate]);

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    setKey("");
    navigate("/login?next=/admin", { replace: true });
  };

  if (!key) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-home-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">Admin</div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-home-title"
          >
            <BoxedTitle size="lg" thick>
              Admin.
            </BoxedTitle>
          </h1>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-[#6b6b6b]">
            Internal tools. Pick a section below — or sign out from the
            navbar when you&apos;re done.
          </p>
        </div>
        <button
          onClick={onLock}
          className="vbb-btn"
          data-testid="admin-lock-btn"
        >
          Lock
        </button>
      </div>

      {error && (
        <div
          className="mt-8 p-4 border border-[#e34234] text-[#e34234] font-mono-tiny"
          data-testid="admin-error-banner"
        >
          {error}
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Inbox card */}
        <Link
          to="/admin/inbox"
          className="block border border-[#0a0a0a] p-8 bg-white hover:bg-[#fafafa] transition-colors"
          data-testid="admin-home-inbox-card"
        >
          <div className="vbb-overline">Contact submissions</div>
          <h2 className="font-display uppercase text-3xl mt-4 tracking-tight">
            Inbox.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2a2a2a]">
            Read, filter, and triage messages from the public contact
            form. Auto-marks read on open. Filter by reason or unread.
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div className="font-mono-tiny text-[#6b6b6b]">
              {totalContacts === null ? (
                "Loading…"
              ) : (
                <>
                  {totalContacts} total ·{" "}
                  <span
                    className={
                      unread > 0 ? "text-[#e34234]" : "text-[#6b6b6b]"
                    }
                    data-testid="admin-home-unread-count"
                  >
                    {unread} unread
                  </span>
                </>
              )}
            </div>
            <span className="font-mono-tiny uppercase tracking-wider">
              Open →
            </span>
          </div>
        </Link>

        {/* Market card */}
        <Link
          to="/admin/market"
          className="block border border-[#0a0a0a] p-8 bg-white hover:bg-[#fafafa] transition-colors"
          data-testid="admin-home-market-card"
        >
          <div className="vbb-overline">Market intelligence</div>
          <h2 className="font-display uppercase text-3xl mt-4 tracking-tight">
            Market.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2a2a2a]">
            Competitor tracker and the advisor pricing memo. Future home
            of a separate access key for sensitive market data.
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div className="font-mono-tiny text-[#6b6b6b]">
              {totalCompetitors === null ? (
                "Loading…"
              ) : (
                <>
                  {totalCompetitors} tracked ·{" "}
                  <span
                    className={
                      highThreat > 0 ? "text-[#e34234]" : "text-[#6b6b6b]"
                    }
                    data-testid="admin-home-high-threat-count"
                  >
                    {highThreat} high threat
                  </span>
                </>
              )}
            </div>
            <span className="font-mono-tiny uppercase tracking-wider">
              Open →
            </span>
          </div>
        </Link>

        {/* Essays card */}
        <Link
          to="/admin/essays"
          className="block border border-[#0a0a0a] p-8 bg-white hover:bg-[#fafafa] transition-colors"
          data-testid="admin-home-essays-card"
        >
          <div className="vbb-overline">Working folder</div>
          <h2 className="font-display uppercase text-3xl mt-4 tracking-tight">
            Essays.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2a2a2a]">
            Drafts and published pieces. Markdown-native, per-essay
            bibliography, RSS on publish.
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div
              className="font-mono-tiny text-[#6b6b6b]"
              data-testid="admin-home-essay-count"
            >
              {essayCount === null ? "Loading…" : `${essayCount} in folder`}
            </div>
            <span className="font-mono-tiny uppercase tracking-wider">
              Open →
            </span>
          </div>
        </Link>

        {/* References card */}
        <Link
          to="/admin/references"
          className="block border border-[#0a0a0a] p-8 bg-white hover:bg-[#fafafa] transition-colors"
          data-testid="admin-home-references-card"
        >
          <div className="vbb-overline">Bibliography</div>
          <h2 className="font-display uppercase text-3xl mt-4 tracking-tight">
            References.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2a2a2a]">
            CSL-JSON library. Export as APA or MLA on demand. Attach to
            essays; recommend to public via RSS.
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div
              className="font-mono-tiny text-[#6b6b6b]"
              data-testid="admin-home-reference-count"
            >
              {referenceCount === null
                ? "Loading…"
                : `${referenceCount} tracked`}
            </div>
            <span className="font-mono-tiny uppercase tracking-wider">
              Open →
            </span>
          </div>
        </Link>

        {/* Ideas card */}
        <Link
          to="/admin/ideas"
          className="block border border-[#0a0a0a] p-8 bg-white hover:bg-[#fafafa] transition-colors md:col-span-2"
          data-testid="admin-home-ideas-card"
        >
          <div className="vbb-overline">Private backlog</div>
          <h2 className="font-display uppercase text-3xl mt-4 tracking-tight">
            Ideas.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2a2a2a]">
            Story and essay ideas. Filter by status, promote one to a
            draft essay when you&apos;re ready to work on it.
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div
              className="font-mono-tiny text-[#6b6b6b]"
              data-testid="admin-home-idea-count"
            >
              {ideaCount === null ? "Loading…" : `${ideaCount} in backlog`}
            </div>
            <span className="font-mono-tiny uppercase tracking-wider">
              Open →
            </span>
          </div>
        </Link>

        {/* YC application card */}
        <Link
          to="/admin/yc"
          className="block border border-[#0a0a0a] p-8 bg-white hover:bg-[#fafafa] transition-colors md:col-span-2"
          data-testid="admin-home-yc-card"
        >
          <div className="vbb-overline">Founder</div>
          <h2 className="font-display uppercase text-3xl mt-4 tracking-tight">
            YC application.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#2a2a2a]">
            Question reference and prep notes for the Y Combinator
            Spring 2026 batch. Links to the existing VBB work you can cite,
            deadline reminders, and story hypothesis.
          </p>
          <div className="mt-6 flex items-center justify-between">
            <div className="font-mono-tiny text-[#6b6b6b]">
              Spring 2026 · deadline Jul 27, 8pm PT
            </div>
            <span className="font-mono-tiny uppercase tracking-wider">
              Open →
            </span>
          </div>
        </Link>
      </div>
    </main>
  );
}
